import { useState } from "react";
import { Cpu, Trash2, RotateCcw, Wand2, AlertTriangle, PlusCircle, HelpCircle, Loader2, Sparkles, Layers, Boxes } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import AutoResizeTextarea from "@/components/AutoResizeTextarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BACKEND_URL, uid, CAUTIONARY_TALES, COMPANY_TYPES, DEFAULT_COMPONENT_ROWS, CORE_TO_VALUE, OWNERSHIP_OPTIONS, MATURITY_OPTIONS, REPLACEABLE_OPTIONS, SEVERITY_STYLES } from "./constants";

// ---- Inlined atoms ----
function FieldWand({ tab, field, fieldLabel, current, context, onAnswer, testid }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/suggest-field`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, field, fieldLabel, current, context }),
      });
      if (res.ok) { const d = await res.json(); if (d.answer) { onAnswer(d.answer); toast.success("AI draft ready"); } else toast.info("No draft"); }
      else toast.error("Failed"); } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <Button size="sm" variant="ghost" onClick={run} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={testid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI draft</span>
    </Button>
  );
}
function WandField({ tab, field, label, value, onChange, onBlur, rows = 3, placeholder, context, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <FieldWand tab={tab} field={field} fieldLabel={label} current={value} context={context} onAnswer={(a) => onChange(a)} testid={`${testid}-wand`} />
      </div>
      <AutoResizeTextarea value={value || ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} minRows={rows} maxHeight={560} className="mt-1 text-sm leading-relaxed" placeholder={placeholder} data-testid={testid} />
    </div>
  );
}
function CautionaryTale({ tale, testid }) {
  return (
    <Card className="border-amber-200 bg-amber-50 sticky top-4" data-testid={testid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Cautionary tale · {tale.title}</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-xs text-amber-900">
        <p>{tale.body}</p>
        <p className="font-semibold">Lesson: <span className="font-normal">{tale.lesson}</span></p>
      </CardContent>
    </Card>
  );
}
function GuidancePanel({ warnings, onDismiss, onRefresh, busy }) {
  if (!warnings || !warnings.length) return null;
  return (
    <Card className="border-teal-200 bg-teal-50/50" data-testid="tech-guidance">
      <CardHeader className="pb-2"><div className="flex items-center justify-between">
        <CardTitle className="text-sm text-teal-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Strategy coach warnings</CardTitle>
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={busy} className="h-7 px-2 text-teal-700" data-testid="tech-guidance-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Re-check</span>
        </Button>
      </div></CardHeader>
      <CardContent className="space-y-2">
        {warnings.map((w) => (
          <div key={w.id} className={`text-xs border px-2 py-1.5 rounded flex items-start gap-2 ${SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.medium}`} data-testid={`tech-warn-${w.id}`}>
            <Badge variant="outline" className="text-[10px] capitalize shrink-0">{w.severity}</Badge>
            <span className="flex-1 leading-snug">{w.message}</span>
            <button onClick={() => onDismiss(w.id)} className="opacity-60 hover:opacity-100" data-testid={`tech-warn-del-${w.id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
function KeyQuestionsSection({ tab, tabData, questions, onSetQuestions, persistTab, testidBase }) {
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const suggest = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/suggest-key-questions`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab, tab_data: tabData, existing: questions.map((q) => q.text) }),
      });
      if (res.ok) {
        const d = await res.json();
        const next = [...questions, ...(d.questions || []).map((t) => ({ id: uid(), text: t }))];
        onSetQuestions(next); persistTab(next);
        if (d.questions?.length) toast.success(`Added ${d.questions.length} AI questions`);
      } else toast.error("Failed"); } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };
  const add = () => { if (!input.trim()) return; const next = [...questions, { id: uid(), text: input.trim() }]; onSetQuestions(next); persistTab(next); setInput(""); };
  const remove = (id) => { const next = questions.filter((q) => q.id !== id); onSetQuestions(next); persistTab(next); };
  return (
    <Card data-testid={`${testidBase}-section`}>
      <CardHeader className="pb-2"><div className="flex items-center justify-between">
        <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-teal-600" /> Key questions</CardTitle>
        <Button size="sm" variant="ghost" onClick={suggest} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid={`${testidBase}-suggest`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI suggest</span>
        </Button>
      </div></CardHeader>
      <CardContent className="space-y-2">
        {questions.length === 0 && <p className="text-xs text-slate-400 italic">No questions yet.</p>}
        {questions.map((q) => (
          <div key={q.id} className="flex items-start gap-2 border-l-2 border-l-teal-300 pl-3 py-1.5" data-testid={`${testidBase}-q-${q.id}`}>
            <span className="text-sm text-slate-700 flex-1">{q.text}</span>
            <button onClick={() => remove(q.id)} className="text-slate-300 hover:text-red-500" data-testid={`${testidBase}-q-del-${q.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex gap-2 pt-2 border-t">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add your own question…" className="text-sm h-9" data-testid={`${testidBase}-input`} />
          <Button size="sm" onClick={add} data-testid={`${testidBase}-add`}><PlusCircle className="w-4 h-4" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---- Component Map Table ----
const CMSel = ({ value, onChange, options, testid }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger className="h-8 text-[11px] border-0 focus-visible:ring-1" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}</SelectContent>
  </Select>
);

function ComponentMapTable({ data, setData, persistTab }) {
  const [busy, setBusy] = useState(false);
  const rows = (data.component_map && data.component_map.length > 0)
    ? data.component_map
    : DEFAULT_COMPONENT_ROWS.map((c, i) => ({ id: `cm_seed_${i}`, component: c, why_matters: "", core_to_value: "", ownership: "", maturity: "", replaceable: "", biggest_risk: "" }));

  const setRows = (nextRows) => { const next = { ...data, component_map: nextRows }; setData(next); persistTab(next); };
  const setRowsNoPersist = (nextRows) => setData({ ...data, component_map: nextRows });
  const updateCell = (id, patch) => setRowsNoPersist(rows.map((r) => r.id === id ? { ...r, ...patch } : r));
  const commit = () => setRows(rows);
  const addRow = () => setRows([...rows, { id: `cm_${Date.now()}`, component: "", why_matters: "", core_to_value: "", ownership: "", maturity: "", replaceable: "", biggest_risk: "" }]);
  const removeRow = (id) => setRows(rows.filter((r) => r.id !== id));

  const aiFill = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/suggest-table`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ table: "component_map", rows, context: { primary_output: data.primary_output, current_lead_programme: data.current_lead_programme, platform_claim: data.platform_claim, company_type: data.company_type, diligence_focus: data.diligence_focus } }),
      });
      if (res.ok) { const d = await res.json(); if (d.rows?.length) { setRows(d.rows); toast.success("Component map filled"); } else toast.info("No rows returned"); }
      else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  const Sel = null; // moved to file scope as CMSel
  const commitAfter = (fn) => { fn(); setTimeout(commit, 0); };

  return (
    <div data-testid="prod-component-map">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">Decompose your technology into value-creating components. Fill each column.</p>
        <Button size="sm" variant="ghost" onClick={aiFill} disabled={busy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="prod-cm-ai-fill">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">AI fill</span>
        </Button>
      </div>
      <div className="border rounded-lg overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-24 gap-0 bg-slate-50 border-b text-[10px] font-medium text-slate-500 uppercase tracking-wide" style={{ gridTemplateColumns: "3fr 3fr 2fr 2fr 1.5fr 2fr 3fr 0.5fr" }}>
            <div className="px-2 py-2 border-r">Component</div>
            <div className="px-2 py-2 border-r">Why it matters</div>
            <div className="px-2 py-2 border-r">Core to value?</div>
            <div className="px-2 py-2 border-r">Owned or controlled?</div>
            <div className="px-2 py-2 border-r">Maturity</div>
            <div className="px-2 py-2 border-r">Replaceable?</div>
            <div className="px-2 py-2 border-r">Biggest risk</div>
            <div className="px-2 py-2"></div>
          </div>
          {rows.map((row) => (
            <div key={row.id} className="grid gap-0 border-b last:border-b-0 hover:bg-slate-50/40" style={{ gridTemplateColumns: "3fr 3fr 2fr 2fr 1.5fr 2fr 3fr 0.5fr" }} data-testid={`prod-cm-row-${row.id}`}>
              <div className="border-r p-1"><Textarea value={row.component} onChange={(e) => updateCell(row.id, { component: e.target.value })} onBlur={commit} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3rem]" data-testid={`prod-cm-comp-${row.id}`} /></div>
              <div className="border-r p-1"><Textarea value={row.why_matters} onChange={(e) => updateCell(row.id, { why_matters: e.target.value })} onBlur={commit} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3rem]" data-testid={`prod-cm-why-${row.id}`} /></div>
              <div className="border-r p-1"><CMSel value={row.core_to_value} onChange={(v) => commitAfter(() => updateCell(row.id, { core_to_value: v }))} options={CORE_TO_VALUE} testid={`prod-cm-ctv-${row.id}`} /></div>
              <div className="border-r p-1"><CMSel value={row.ownership} onChange={(v) => commitAfter(() => updateCell(row.id, { ownership: v }))} options={OWNERSHIP_OPTIONS} testid={`prod-cm-own-${row.id}`} /></div>
              <div className="border-r p-1"><CMSel value={row.maturity} onChange={(v) => commitAfter(() => updateCell(row.id, { maturity: v }))} options={MATURITY_OPTIONS} testid={`prod-cm-mat-${row.id}`} /></div>
              <div className="border-r p-1"><CMSel value={row.replaceable} onChange={(v) => commitAfter(() => updateCell(row.id, { replaceable: v }))} options={REPLACEABLE_OPTIONS} testid={`prod-cm-rep-${row.id}`} /></div>
              <div className="border-r p-1"><Textarea value={row.biggest_risk} onChange={(e) => updateCell(row.id, { biggest_risk: e.target.value })} onBlur={commit} rows={2} className="text-xs border-0 focus-visible:ring-1 resize-none min-h-[3rem]" data-testid={`prod-cm-risk-${row.id}`} /></div>
              <div className="flex items-start justify-center pt-2"><button onClick={() => removeRow(row.id)} className="text-slate-300 hover:text-red-500" data-testid={`prod-cm-del-${row.id}`}><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
          ))}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={addRow} className="mt-2 text-teal-600" data-testid="prod-cm-add"><PlusCircle className="w-4 h-4 mr-1" /> Add component</Button>
    </div>
  );
}

export default function ProductTab({ data, setData, persist }) {
  const set = (patch) => setData({ ...data, ...patch });
  const setField = (k) => (v) => set({ [k]: v });
  const persistTab = (nextProd) => persist({ product: nextProd });

  const [gBusy, setGBusy] = useState(false);
  const checkGuidance = async () => {
    setGBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/technology/check-guidance`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ tab: "product", tab_data: data }),
      });
      if (res.ok) { const d = await res.json(); const next = { ...data, guidance: d.warnings || [] }; setData(next); persistTab(next); if (d.warnings?.length) toast.success(`${d.warnings.length} coach warnings`); else toast.info("No warnings"); }
      else toast.error("Failed");
    } catch (e) { toast.error("Failed"); }
    setGBusy(false);
  };
  const dismissWarn = (id) => { const next = { ...data, guidance: (data.guidance || []).filter((w) => w.id !== id) }; setData(next); persistTab(next); };

  const resetTab = () => {
    if (window.confirm("Reset the Product tab? This clears all answers, tables and questions.")) {
      const empty = { primary_output: "", current_lead_programme: "", platform_claim: "", diligence_focus: "", company_type: "", component_map: [], near_term_value: "", platform_proof: "", reusable: "", asset_specific: "", external_focus: "", key_questions: [], guidance: [] };
      setData(empty); persistTab(empty); toast.success("Product tab reset");
    }
  };

  const ctx = { primary_output: data.primary_output, current_lead_programme: data.current_lead_programme, platform_claim: data.platform_claim, company_type: data.company_type };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="product-tab">
      <div className="lg:col-span-2 space-y-4">
        <GuidancePanel warnings={data.guidance || []} onDismiss={dismissWarn} onRefresh={checkGuidance} busy={gBusy} />

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Cpu className="w-4 h-4 text-teal-600" /> A · Company type</CardTitle>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={checkGuidance} disabled={gBusy} className="h-7 px-2 text-amber-600 hover:bg-amber-50" data-testid="prod-guidance-btn">
                  {gBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1 text-xs">Coach check</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={resetTab} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="prod-reset-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">Decompose your technology into what actually creates value.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="product" field="primary_output" label="Primary output" value={data.primary_output} onChange={setField("primary_output")} onBlur={persist} placeholder="e.g. therapeutic asset, discovery platform, delivery system, dataset, diagnostic-linked therapy, service/collaboration model, hybrid." context={ctx} testid="prod-primary" />
            <WandField tab="product" field="current_lead_programme" label="Current lead programme" value={data.current_lead_programme} onChange={setField("current_lead_programme")} onBlur={persist} placeholder="Describe the lead asset/programme, if there is one." context={ctx} testid="prod-lead" />
            <WandField tab="product" field="platform_claim" label="Platform claim" value={data.platform_claim} onChange={setField("platform_claim")} onBlur={persist} placeholder="What does the platform repeatedly enable, discover, generate or validate?" context={ctx} testid="prod-platform" />
            <WandField tab="product" field="diligence_focus" label="What would someone actually diligence?" value={data.diligence_focus} onChange={setField("diligence_focus")} onBlur={persist} placeholder="e.g. lead asset data, repeatability of platform, target validation, molecule quality, IP, data advantage, manufacturability." context={ctx} testid="prod-diligence" />
            <div>
              <Label className="text-sm font-medium text-slate-800">Company type</Label>
              <Select value={data.company_type || ""} onValueChange={(v) => { const next = { ...data, company_type: v }; setData(next); persistTab(next); }}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="prod-company-type"><SelectValue placeholder="Select company type…" /></SelectTrigger>
                <SelectContent>{COMPANY_TYPES.map((t) => <SelectItem key={t.id} value={t.id} className="text-sm">{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Boxes className="w-4 h-4 text-teal-600" /> B · Component map</CardTitle>
          <p className="text-xs text-slate-500">Which components create value, which are fragile, which are partner-dependent.</p></CardHeader>
          <CardContent><ComponentMapTable data={data} setData={setData} persistTab={persistTab} /></CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-800 flex items-center gap-2"><Layers className="w-4 h-4 text-teal-600" /> C · Asset vs platform logic</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <WandField tab="product" field="near_term_value" label="Where is the near-term value?" value={data.near_term_value} onChange={setField("near_term_value")} onBlur={persist} placeholder="Is the near-term value in the lead asset, the platform, or both?" context={ctx} testid="prod-near-term" />
            <WandField tab="product" field="platform_proof" label="What proves the platform?" value={data.platform_proof} onChange={setField("platform_proof")} onBlur={persist} placeholder="What evidence would show that this is repeatable beyond one programme?" context={ctx} testid="prod-plat-proof" />
            <WandField tab="product" field="reusable" label="What is reusable across future programmes?" value={data.reusable} onChange={setField("reusable")} onBlur={persist} placeholder="e.g. data, discovery workflow, assays, target-selection method, chemistry, delivery system." context={ctx} testid="prod-reusable" />
            <WandField tab="product" field="asset_specific" label="What is specific to the lead asset?" value={data.asset_specific} onChange={setField("asset_specific")} onBlur={persist} placeholder="e.g. target, indication, molecule, dataset, assay, biomarker." context={ctx} testid="prod-asset-spec" />
            <WandField tab="product" field="external_focus" label="What would pharma or investors pay attention to first?" value={data.external_focus} onChange={setField("external_focus")} onBlur={persist} placeholder="Describe what an external party would care about before engaging seriously." context={ctx} testid="prod-external" />
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-1"><CautionaryTale tale={CAUTIONARY_TALES.product} testid="prod-cautionary" /></div>
    </div>
  );
}
